import { Trans, useTranslation } from "react-i18next";
import { SubscriptionProduct } from "gql/generated.graphql";
import {
  ProductTheme,
  SubscriptionChangeMode,
  TrialUpgradeDetails,
} from "../SubscriptionChangeConfirmationModalV2.interfaces";
import {
  formatEndDate,
  getRecurringBillingLabel,
  getRecurringPeriodLabel,
} from "../SubscriptionChangeConfirmationModalV2.helpers";
import { formatDateString } from "pages/ProfilePage/components/ProfileBilling/ProfileBilling.helpers";

interface PlanInfoCardProps {
  newProduct: SubscriptionProduct;
  displayName: string;
  productTheme: ProductTheme;
  mode: SubscriptionChangeMode;
  isPro: boolean;
  currentPeriodEndAt?: Date | string;
  trial?: TrialUpgradeDetails;
  currentPlanName?: string;
}

export default function PlanInfoCard({
  newProduct,
  displayName,
  productTheme,
  mode,
  isPro,
  currentPeriodEndAt,
  trial,
  currentPlanName,
}: PlanInfoCardProps) {
  const { t } = useTranslation("pages", {
    keyPrefix: "profilePage.billing.trialUpgrade",
  });
  const { t: tc } = useTranslation(["pages", "common"]);
  const periodLabel = getRecurringPeriodLabel(newProduct.recurringType, tc);
  const billingLabel = getRecurringBillingLabel(newProduct.recurringType, tc);
  const trialEndDate = trial ? formatDateString(trial.endDate) : "";

  return (
    <div
      className={`border-l-4 rounded-lg ${trial ? "p-3 mb-4" : "p-4 mb-6"}`}
      style={{
        borderLeftColor: productTheme.bg,
        backgroundColor: "#F8FAFC",
      }}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-start justify-between mb-1">
            <h3 className="text-base font-semibold text-slate-800 mb-1">
              {displayName} {periodLabel}
            </h3>
            {isPro && (
              <span
                className="px-4 py-1 text-xs font-semibold text-white rounded-xl ml-2"
                style={{ backgroundColor: productTheme.bg }}
              >
                {tc("common:labels.popular")}
              </span>
            )}
          </div>
          {trial ? (
            <p className="text-sm text-slate-500">
              {t("intro", {
                days: trial.days,
                plan: displayName,
                date: trialEndDate,
                previousPlan: currentPlanName || t("currentPlanFallback"),
              })}
            </p>
          ) : (
            <p className="text-sm text-slate-500">
              {mode === "upgrade" ? (
                <Trans
                  i18nKey="pages:profilePage.billing.changeConfirm.upgradeDescription"
                  values={{ plan: displayName, billing: billingLabel }}
                  components={{ bold: <b /> }}
                />
              ) : currentPeriodEndAt ? (
                <Trans
                  i18nKey="pages:profilePage.billing.changeConfirm.downgradeDescriptionWithDate"
                  values={{
                    plan: displayName,
                    billing: billingLabel,
                    date: formatEndDate(currentPeriodEndAt),
                  }}
                  components={{ bold: <b /> }}
                />
              ) : (
                <Trans
                  i18nKey="pages:profilePage.billing.changeConfirm.downgradeDescription"
                  values={{ plan: displayName, billing: billingLabel }}
                  components={{ bold: <b /> }}
                />
              )}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
